import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { LanguageIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@time-theft-analyzer/shared';
import { LanguageSelect } from './LanguageSelect';
import { LANGUAGE_LABELS, type SupportedLanguage } from './supportedLanguages';

interface UserLanguagePickerProps {
  // 'inline' renders the dropdown directly; 'menu' renders a label + dropdown stacked for use inside an existing dropdown panel.
  variant?: 'inline' | 'menu';
  className?: string;
}

export const UserLanguagePicker: React.FC<UserLanguagePickerProps> = ({
  variant = 'inline',
  className,
}) => {
  const { t } = useTranslation(['admin', 'common']);
  const {
    user,
    company,
    effectiveLanguage,
    availableLanguages,
    setUserLanguage,
  } = useAuth();
  const [saving, setSaving] = useState(false);

  // Hide picker entirely when the company disallows user overrides.
  if (!company || company.allowUserLanguageOverride === false) {
    return null;
  }

  const currentValue: SupportedLanguage | '' = (user?.languagePreference ??
    '') as SupportedLanguage | '';

  const handleChange = async (next: SupportedLanguage | '') => {
    const lang = next === '' ? null : next;
    const previous = user?.languagePreference ?? null;
    setSaving(true);
    try {
      await setUserLanguage(lang);
      toast.success(t('common:toast.languageUpdated'));
    } catch (err: any) {
      // Revert on failure by restoring the previous selection at the server level.
      try {
        await setUserLanguage(previous);
      } catch {
        /* best-effort revert */
      }
      toast.error(
        err?.response?.data?.message || t('common:toast.languageUpdateFailed')
      );
    } finally {
      setSaving(false);
    }
  };

  if (variant === 'menu') {
    return (
      <div className={className ?? 'px-3 py-2'}>
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
          {t('admin:userMenu.languageSectionLabel')}
        </div>
        <LanguageSelect
          value={currentValue}
          onChange={(lang) => handleChange(lang)}
          available={availableLanguages}
          disabled={saving}
          includeUseCompanyDefault
          useCompanyDefaultLabel={t('common:labels.useCompanyDefault')}
          ariaLabel={t('admin:userMenu.languageSectionLabel')}
        />
        <div className="text-xs text-gray-500 mt-1">
          {t('admin:userMenu.languageHelpText')}
        </div>
      </div>
    );
  }

  // Inline variant: compact label + select for header bars.
  return (
    <div className={className ?? 'flex items-center gap-2'}>
      <LanguageIcon className="h-4 w-4 text-tk-text-secondary" aria-hidden />
      <span className="sr-only">{t('common:labels.language')}</span>
      <LanguageSelect
        value={currentValue}
        onChange={(lang) => handleChange(lang)}
        available={availableLanguages}
        disabled={saving}
        includeUseCompanyDefault
        useCompanyDefaultLabel={
          LANGUAGE_LABELS[effectiveLanguage as SupportedLanguage]
            ? `${t('common:labels.useCompanyDefault')} (${LANGUAGE_LABELS[effectiveLanguage as SupportedLanguage]})`
            : t('common:labels.useCompanyDefault')
        }
        className="text-sm px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50"
        ariaLabel={t('common:labels.language')}
      />
    </div>
  );
};

export default UserLanguagePicker;
